import { Inject, Injectable, OnDestroy } from '@angular/core';
import { Observable, from, of, map, catchError } from 'rxjs';
import { Router } from '@angular/router';
import { fetchAuthSession } from 'aws-amplify/auth';
import { PolicyService } from './policy.service';

/**
 * A bounded `fetchAuthSession()` that did not answer in time.
 *
 * Distinct from "there is no session", and the distinction is the whole point: the stored tokens may
 * be perfectly valid and the call merely slow. Callers must NOT read this as proof the user is signed
 * out — clearing credentials on it turns a transient stall into a forced re-login, which is exactly
 * what customers hit on 2026-08-26 while the service worker was re-fetching a whole bundle over a
 * congested connection.
 */
export class AuthSessionTimeoutError extends Error {
  constructor(ms: number) {
    super(`Auth session request timed out after ${ms}ms`);
    this.name = 'AuthSessionTimeoutError';
  }
}

/**
 * Outcome of an access-token lookup, with "no session" and "could not determine" kept apart.
 *
 * `getAccessToken()` collapses both to `null`, which is fine for callers that only want a token to
 * attach. It is NOT fine for the one caller that decides whether to destroy the session — see
 * {@link AuthService.resolveAccessToken}.
 */
export type AccessTokenResult =
  | { status: 'token'; token: string }
  | { status: 'no-session' }
  | { status: 'undetermined'; error: unknown };

/**
 * What a caller should do after asking whether the stored session is usable.
 *
 * `timeout` means the lookup never answered. The stored refresh token may still be valid
 * even when the access token's `exp` passed during the wait. Do not delete those records,
 * and do not send the user straight back to `/callback` — that pair is a redirect loop.
 * `signed-out` is a real answer (no session, or a completed lookup whose token is not
 * usable) and the login screen may clear the stored records.
 */
export type SessionClassification = 'authenticated' | 'timeout' | 'signed-out';

/** Query value login reads to skip the automatic `/callback` hop after a timed-out lookup. */
export const SESSION_CHECK_TIMEOUT = 'timeout';

@Injectable({ providedIn: 'root' })
export class AuthService implements OnDestroy {
  constructor(
    @Inject('environment') private readonly environment,
    private router: Router,
    private policyService: PolicyService
  ) {}

  ngOnDestroy(): void {
    // No subscriptions to clean up anymore
  }

  /**
   * In-flight Amplify session fetch, shared across concurrent callers. When the access token
   * is expired and the network is unreachable, every in-flight HTTP request would otherwise kick
   * off its own fetchAuthSession()/token-refresh; sharing one bounded call collapses that pile-up,
   * which is a key contributor to the page freezing when offline or after the JWT expires.
   */
  private sessionInFlight: ReturnType<typeof fetchAuthSession> | null = null;
  private static readonly SESSION_TIMEOUT_MS = 5000;
  /**
   * Budget for the single retry after a timeout. Longer than the first attempt on purpose: the thing
   * that made the first one time out is usually contention that is still clearing, and the retry is
   * the last word before a caller may conclude the session is unusable.
   */
  private static readonly SESSION_RETRY_TIMEOUT_MS = 10000;

  /**
   * Guards the burst of logout() calls a wave of failed/401 requests triggers. Clearing
   * credentials is cheap and idempotent, but each router navigation re-runs guards and change
   * detection, so we collapse the redundant redirects into a single navigation.
   */
  private redirectingToLogin = false;

  private getSession(): ReturnType<typeof fetchAuthSession> {
    if (this.sessionInFlight) {
      return this.sessionInFlight;
    }
    const inFlight = this.fetchSessionWithOneRetry();
    // Release the shared handle once settled so later calls fetch a fresh session — we only
    // dedupe truly concurrent callers, never cache a stale or expired result.
    inFlight
      .finally(() => {
        if (this.sessionInFlight === inFlight) {
          this.sessionInFlight = null;
        }
      })
      // The cleanup chain is a derived promise nobody awaits, so a rejected session would surface as
      // an unhandled rejection here as well as at the real caller. Swallow it on this branch only —
      // `inFlight` itself is what callers get, and it still rejects for them.
      .catch(() => undefined);
    this.sessionInFlight = inFlight;
    return inFlight;
  }

  /**
   * One bounded attempt, then — on a timeout only — one more on a longer budget.
   *
   * Retrying only the timeout is deliberate. Any other rejection from Amplify is an answer (a revoked
   * or expired refresh token, a malformed store) and repeating it just doubles the wait before the
   * caller acts on it. A timeout is the absence of an answer, and is the one failure a second attempt
   * can actually convert into one.
   */
  private async fetchSessionWithOneRetry(): ReturnType<typeof fetchAuthSession> {
    try {
      return await this.withTimeout(fetchAuthSession(), AuthService.SESSION_TIMEOUT_MS);
    } catch (error) {
      if (!(error instanceof AuthSessionTimeoutError)) {
        throw error;
      }
      return await this.withTimeout(fetchAuthSession(), AuthService.SESSION_RETRY_TIMEOUT_MS);
    }
  }

  private withTimeout<T>(promise: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => reject(new AuthSessionTimeoutError(ms)), ms);
      promise.then(
        (value) => { clearTimeout(timer); resolve(value); },
        (error) => {
          clearTimeout(timer);
          reject(error instanceof Error ? error : new Error(String(error)));
        }
      );
    });
  }

  /**
   * The configured client's LastAuthUser access token, when its `exp` is still in the future.
   *
   * Synchronous on purpose. `fetchAuthSession()` is the wrong tool for deciding whether the login
   * screen may paint: with OAuth configured it will try to refresh, and a refresh that does not
   * answer is bounded at 5s plus a 10s retry. That wait was the login page's LCP.
   *
   * Only that one key counts. Impersonation leaves previous users' tokens in storage, and a scan
   * that prefers LastAuthUser but then falls through would treat someone else's unexpired token as
   * this session. Amplify would then load a different user and bounce back to login.
   */
  readUnexpiredAccessToken(): string | null {
    const clientId = this.environment?.cognito_clientid;
    if (!clientId) {
      return null;
    }

    let storage: Storage;
    try {
      storage = window.localStorage;
    } catch {
      return null;
    }
    if (!storage) {
      return null;
    }

    try {
      const user = storage.getItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`);
      if (!user) {
        return null;
      }
      const token = storage.getItem(`CognitoIdentityServiceProvider.${clientId}.${user}.accessToken`);
      if (token && !this.isTokenExpired(token)) {
        return token;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Drop Amplify's persisted Cognito records without calling `signOut()`.
   *
   * `signOut()` with the OAuth block in `Amplify.configure` navigates the browser to the Cognito
   * hosted UI `/logout` endpoint, which then redirects back to `/auth/login`. That round trip is
   * part of the next document's LCP. Local removal is what `logout()` already does. It does not
   * reset AuthenticatorService; the login screen does that itself, still without `signOut()`.
   */
  clearPersistedCognitoSession(): void {
    try {
      if (!window.localStorage) {
        return;
      }
      Object.keys(window.localStorage).forEach(key => {
        if (key.startsWith('CognitoIdentityServiceProvider') ||
            key.startsWith('amplify-') ||
            key.includes('Cognito')) {
          window.localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error('Error clearing persisted Cognito session:', error);
    }
  }

  /**
   * Redirect to login page
   */
  login(redirectUrl: string | null = null): void {
    this.redirectToLogin(redirectUrl);
  }

  /**
   * Check if user is currently logged in using Amplify session
   */
  isLoggedIn(): Observable<boolean> {
    // getSession() already bounds the wait so Amplify/Cognito being unreachable can't hang us.
    return from(this.getSession()).pipe(
      map((session) => {
        // Check if we have valid tokens
        return !!(session.tokens?.accessToken && !this.isTokenExpired(session.tokens.accessToken));
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Logout user using Amplify and redirect to login
   */
  logout(redirect: boolean = true): void {
    // Local clear, not Amplify signOut(): signOut() with OAuth configured redirects to the
    // Cognito hosted UI and back, which is the login page's LCP.
    this.clearPersistedCognitoSession();
    try {
      if (window.sessionStorage) {
        window.sessionStorage.clear();
      }
    } catch (error) {
      console.error('Error during logout:', error);
      // Continue with logout even if clearing fails
    }

    // Outside the storage try: policy pending is cached in-memory for the SPA lifetime, and logout
    // does not reload the page. Always drop it — a storage clear failure must not leave the next
    // user in this tab with the previous session's gate state.
    this.policyService.clearCache();

    // Redirect to login page — once. A failing-request storm calls logout() many times; without
    // this guard each call queues another navigation, re-running guards/change detection.
    if (!redirect || this.redirectingToLogin || this.router.url.startsWith('/auth/login')) {
      return;
    }
    this.redirectingToLogin = true;
    this.router.navigate(['/auth/login']).finally(() => {
      this.redirectingToLogin = false;
    });
  }

  /**
   * Access token, with the reason for its absence preserved.
   *
   * Use this — not {@link getAccessToken} — anywhere the answer decides whether to sign the user out.
   * `undetermined` means we never got an answer and the stored credentials are still to be trusted;
   * treating it as `no-session` is what forces a spurious re-login. Note the narrow classification:
   * ONLY a timeout is undetermined. Every other Amplify rejection stays `no-session`, so a genuinely
   * revoked or expired refresh token still sends the user to the login screen as before, rather than
   * stranding them in a loop of failing requests.
   *
   * Never rejects.
   */
  async resolveAccessToken(): Promise<AccessTokenResult> {
    try {
      const session = await this.getSession();
      const token = session.tokens?.accessToken?.toString();
      return token ? { status: 'token', token } : { status: 'no-session' };
    } catch (error) {
      if (error instanceof AuthSessionTimeoutError) {
        console.warn('Auth session lookup timed out; keeping credentials.', error);
        return { status: 'undetermined', error };
      }
      console.error('Error getting access token:', error);
      return { status: 'no-session' };
    }
  }

  /**
   * Get access token for use in HTTP requests.
   *
   * Collapses "no session" and "could not determine" to `null`. Fine for callers that only want a
   * token to attach; see {@link resolveAccessToken} if the difference matters.
   */
  async getAccessToken(): Promise<string | null> {
    const result = await this.resolveAccessToken();
    return result.status === 'token' ? result.token : null;
  }

  /**
   * Get ID token for user identification
   */
  async getIdToken(): Promise<string | null> {
    try {
      const session = await this.getSession();
      return session.tokens?.idToken?.toString() || null;
    } catch (error) {
      console.error('Error getting ID token:', error);
      return null;
    }
  }

  /**
   * Whether the stored session is usable, without collapsing a timeout into "signed out".
   *
   * `isLoggedIn()` maps every failure to `false`. The callback used that to send people back to
   * login, and login — seeing the unexpired access token still in storage — sent them to
   * `/callback` again. A timeout must stay distinct so that hop can stop.
   *
   * Every undetermined lookup is a timeout, including one whose access token expires while
   * the 5s + 10s attempts are in flight. Rechecking `exp` here and calling that `signed-out`
   * makes both callbacks delete a refresh token Cognito never rejected.
   */
  async classifySession(): Promise<SessionClassification> {
    const result = await this.resolveAccessToken();
    if (result.status === 'undetermined') {
      return 'timeout';
    }
    if (result.status === 'token' && !this.isTokenExpired(result.token)) {
      return 'authenticated';
    }
    return 'signed-out';
  }

  /**
   * Redirect to login page with optional redirect URL.
   *
   * `sessionCheckTimedOut` tells login the last `/callback` lookup never answered. Login must
   * not clear storage and must not navigate straight back to `/callback`, even if the access
   * token expired during the wait. The refresh token may still be valid.
   */
  redirectToLogin(
    redirectUrl: string | null = null,
    errorDescription: string | null = null,
    options?: { sessionCheckTimedOut?: boolean }
  ): void {
    const loginUrl = '/auth/login';
    const queryParams: Record<string, string> = {};
    if (redirectUrl) {
      queryParams['redirectUrl'] = encodeURIComponent(redirectUrl);
    }
    if (errorDescription) {
      queryParams['error'] = encodeURIComponent(errorDescription);
    }
    if (options?.sessionCheckTimedOut) {
      queryParams['sessionCheck'] = SESSION_CHECK_TIMEOUT;
    }
    this.router.navigate([loginUrl], { queryParams });
  }

  /**
   * Check if user has any of the required authorities
   */
  arrayHasAnyAuthority(userAuthorities: string[], authorities: string[]): boolean {
    if (!userAuthorities || !Array.isArray(userAuthorities)) {
      return false;
    }
    if (!authorities?.length) {
      return true;
    }
    const intersection = userAuthorities.filter(x => authorities.includes(x));
    return intersection.length > 0;
  }

  /**
   * Extract claims from a JWT token
   * @param token The JWT token string
   * @returns The decoded token payload/claims, or null if invalid
   */
  getTokenClaims(token: string): Record<string, any> | null {
    if (!token || typeof token !== 'string') {
      return null;
    }

    try {
      // Validate JWT structure: should have 3 parts separated by dots
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.warn('Invalid JWT structure: expected 3 parts separated by dots, got', parts.length);
        return null;
      }

      if (!parts[1] || parts[1].trim().length === 0) {
        console.warn('Invalid JWT: payload part is empty');
        return null;
      }

      // Validate base64 encoding before decoding
      const base64Payload = parts[1];
      // Base64 should only contain A-Z, a-z, 0-9, +, /, and = for padding
      if (!/^[A-Za-z0-9+/=]+$/.test(base64Payload)) {
        console.warn('Invalid JWT: payload contains invalid base64 characters');
        return null;
      }

      // Decode base64 and parse JSON
      let decodedPayload: string;
      try {
        decodedPayload = atob(base64Payload);
      } catch (base64Error) {
        console.warn('Failed to decode base64 payload:', base64Error);
        return null;
      }

      // Validate that decoded payload is valid JSON
      let payload: Record<string, any>;
      try {
        payload = JSON.parse(decodedPayload);
      } catch (jsonError) {
        console.warn('Failed to parse JSON payload:', jsonError);
        return null;
      }

      // Validate payload structure (should be an object)
      if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) {
        console.warn('Invalid JWT payload: expected object, got', typeof payload);
        return null;
      }

      return payload;
    } catch (error) {
      console.error('Unexpected error decoding token:', error);
      return null;
    }
  }

  /**
   * Get impersonation information from the current access token
   * @returns Object with isImpersonated flag and impersonatedBy email, or null if not impersonated or error
   */
  async getImpersonationInfo(): Promise<{ isImpersonated: boolean; impersonatedBy: string | null } | null> {
    try {
      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        return { isImpersonated: false, impersonatedBy: null };
      }

      const claims = this.getTokenClaims(accessToken);
      if (!claims) {
        return { isImpersonated: false, impersonatedBy: null };
      }

      const isImpersonated = claims.is_impersonated === 'true' || claims.is_impersonated === true;
      
      return {
        isImpersonated,
        impersonatedBy: isImpersonated ? (claims.impersonated_by || null) : null
      };
    } catch (error) {
      console.error('Error getting impersonation info:', error);
      return { isImpersonated: false, impersonatedBy: null };
    }
  }

  /**
   * True when the token cannot be used: missing, undecodable, or without a finite `exp` still
   * in the future. A malformed record must not count as a live session — login would hide the
   * sign-in form and send the browser to `/callback`, which then bounces back here.
   */
  private isTokenExpired(token: unknown): boolean {
    if (!token) {
      return true;
    }

    try {
      const exp = typeof token === 'string'
        ? this.expirationFromJwt(token)
        : (token as { payload?: { exp?: unknown } }).payload?.exp;
      return typeof exp !== 'number' || !Number.isFinite(exp) || Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  }

  private expirationFromJwt(token: string): unknown {
    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) {
      return undefined;
    }
    const payload = this.decodeJwtPayload(parts[1]);
    return payload?.exp;
  }

  /** JWT payloads are base64url, which `atob` does not accept until they are padded standard base64. */
  private decodeJwtPayload(segment: string): { exp?: unknown } {
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/');
    const pad = (4 - (base64.length % 4)) % 4;
    const json = atob(base64 + '='.repeat(pad));
    return JSON.parse(json);
  }
}
